/*
 * Copyright (c) 2024. Dave van Rijn Development
 */
import './style/dvrdSelect.scss';

import React, {ReactElement, useEffect, useMemo, useRef, useState} from 'react';
import classNames from 'classnames';
import {ChangeFunction, ErrorType, SelectItemShape} from '../util/interfaces';
import {hasHover, stopPropagation} from '../util/controlUtil';
import AwesomeIcon from '../icon/awesomeIcon';

interface Props {
    onChange: (selected: Array<string | number>, submit: boolean) => VoidFunction;
    onChangeSearch: ChangeFunction;
    selected: Array<string | number>;
    items: Array<SelectItemShape<number | string>>;
    disabled?: boolean;
    error?: ErrorType;
    label?: string;
    className?: string;
    labelClassName?: string;
    valueClassName?: string;
    arrowClassName?: string;
    errorClassName?: string;
    itemContainerClassName?: string;
    itemClassName?: string;
    itemsPosition: 'top' | 'bottom';
    selectOnly: boolean;
    searchValue: string;
    optionsContainerHeight: number | string;
    placeholder?: string;
    submitOnClose?: boolean;
}

const transitionDuration = 200; // ms

const useIsFirstRender = () => {
    const isFirstRender = useRef(true);

    useEffect(() => {
        isFirstRender.current = false;
    }, []);

    return isFirstRender.current;
};

export default function DvrdMultiSelect(props: Props) {
    const {
        error, disabled, selected, onChange, onChangeSearch, items, optionsContainerHeight, label, labelClassName,
        selectOnly, valueClassName, searchValue, arrowClassName, itemClassName, itemsPosition, itemContainerClassName,
        errorClassName, className, placeholder, submitOnClose
    } = props;
    const isFirstRender = useIsFirstRender();
    const [open, setOpen] = useState(false);
    const [searchFocused, setSearchFocused] = useState(false);
    const container = useRef<HTMLDivElement>(null);
    const hasError = useMemo(() => !!error, [error]);
    const itemsContainerStyle = useMemo(() => {
        let maxHeight: string | number = optionsContainerHeight;
        if (typeof maxHeight === 'number') maxHeight = `${maxHeight}px`;
        return {maxHeight: maxHeight};
    }, [optionsContainerHeight])

    function onClickElement(evt: React.MouseEvent) {
        stopPropagation(evt);
        if (disabled) return;
        setOpen(!open);
    }

    function onClickItem(_value: string | number) {
        return function (evt: React.MouseEvent) {
            stopPropagation(evt);
            if (selected.includes(_value))
                onChange(selected.filter((value: string | number) => value !== _value), !submitOnClose)();
            else onChange(selected.concat(_value), !submitOnClose)();
        }
    }

    function onFocusInput() {
        setSearchFocused(true);
        setOpen(true);
    }

    function onBlurInput() {
        window.setTimeout(() => {
            setSearchFocused(false);
            onChangeSearch({target: {value: ''}});
        }, transitionDuration);
    }

    function getLongestValue(): string {
        let longest: string = '';
        for (const item of items) {
            const stringLabel = item.label.toString();
            if (stringLabel.length > longest.length)
                longest = stringLabel;
        }
        return longest;
    }

    function getValueLabel(): string {
        let valueLabel = '';
        let extra = 0;
        for (const item of items) {
            if (selected.includes(item.value)) {
                if (valueLabel.length) extra++;
                else valueLabel = getItemLabel(item);
            }
        }
        if (!valueLabel.length) return placeholder ?? '';
        if (extra) valueLabel += ` +${extra}`
        return valueLabel;
    }

    function getItemLabel(item: SelectItemShape): string {
        const {label} = item;
        if (['string', 'number'].includes(typeof label)) return label.toString();
        else if (!!item.valueLabel) return item.valueLabel;
        return item.value.toString();
    }

    function renderLabel() {
        if (!label) return null;
        return <label className={classNames('dvrd-select-label', labelClassName)}
                      onClick={onClickElement}>{label}</label>
    }

    function renderValue() {
        const valueLabel = getValueLabel();
        if (selectOnly)
            return (
                <div style={{display: 'flex', flexDirection: 'column'}}>
                    <label className={classNames('dvrd-select-value', valueClassName)}
                           onClick={onClickElement}>{valueLabel}</label>
                    <label style={{height: 0, visibility: 'hidden'}}>{getLongestValue()}</label>
                </div>
            );
        const value = searchFocused ? searchValue : valueLabel;
        return <input className='dvrd-select-search' value={value} onChange={onChangeSearch} disabled={disabled}
                      onFocus={onFocusInput} onBlur={onBlurInput} onClick={stopPropagation}/>
    }

    function renderArrow() {
        return <AwesomeIcon name='chevron-down' onClick={onClickElement}
                            className={classNames('dvrd-select-arrow', open && 'open', arrowClassName)}/>;
    }

    function renderItems() {
        const _items = items.filter((item: SelectItemShape) => {
            if (item.selectable === false) return false;
            if (typeof item.label === 'string') return item.label.length > 0;
            return true;
        });
        return (
            <div
                className={classNames('dvrd-select-items', (open && !disabled) && 'open', itemsPosition,
                    itemContainerClassName)} style={itemsContainerStyle}>
                {_items.map(renderItem)}
            </div>
        )
    }

    function renderItem(item: SelectItemShape, index: number) {
        const {value, selectableItemLabel} = item;
        const isSelected = selected.includes(value);
        const label = selectableItemLabel ?? item.label;
        if (['number', 'string'].includes(typeof label)) return (
            <label key={index} className={classNames('dvrd-select-item', isSelected && 'selected', itemClassName)}
                   onClick={onClickItem(value)}>{isSelected &&
                <AwesomeIcon name='check' className='check-icon'/>} {label}</label>
        );
        const labelElement: ReactElement<any> = label as ReactElement;
        const onClick = (evt: React.MouseEvent) => {
            onClickItem(value)(evt);
            labelElement.props.onClick?.(evt);
        };
        const className = classNames(labelElement.props.className, 'dvrd-select-item', isSelected && 'selected', itemClassName);
        return React.cloneElement(labelElement, {...labelElement.props, onClick, className, key: index});
    }

    function renderError() {
        if (!hasError) return null;
        return <label className={classNames('dvrd-select-error', errorClassName)}>{error}</label>
    }

    function addClickListener() {
        document.addEventListener('click', clickListener);
    }

    function removeClickListener() {
        document.removeEventListener('click', clickListener);
    }

    function clickListener() {
        if (!hasHover(container.current)) setOpen(false);
    }

    useEffect(() => {
        if (open) addClickListener();
        else {
            removeClickListener();
            if (submitOnClose && !isFirstRender) onChange(selected, true)();
        }
        return function () {
            removeClickListener();
        }
    }, [open]);

    return (
        <div className={classNames('dvrd-select-container', 'multi', disabled && 'disabled', open && 'open',
            hasError && 'error', className)} ref={container} onClick={onClickElement}>
            {renderLabel()}
            <div className={classNames('content-container', !selectOnly && 'search')}>
                {renderValue()}
                {renderArrow()}
            </div>
            {renderItems()}
            {renderError()}
        </div>
    )
}